import { CardShell, SampleDataNotice } from '@atlas/ui-components'
import { Chip } from '@atlas/ui-primitives'
import { Eyebrow } from '@/components/editorial/PageHeader'
import { humanizeCode } from '@/lib/labels'
import { SAMPLE_HERO_LABEL, SAMPLE_TAGLINE } from './brand-page-sample'

/**
 * The Brand page's hero band: logo slot, `BRAND · <BUSINESS UNIT>` eyebrow, serif
 * name, tagline, and the chip row.
 *
 * Real: name, business unit, species, type, and — when the brand supplies them — its logo
 * and tagline. A chip whose tags are ABSENT is omitted rather than filled, and an absent
 * logo or tagline falls back to the placeholder it replaced: an unsupplied brand shows the
 * gap, which is the page's job.
 */
export interface BrandHeroProps {
	name: string
	businessUnit: string | null
	/** Resolved `species` tag labels; empty when the brand carries none. */
	species: string[]
	/** Resolved product type/class tag labels; empty when the brand carries none. */
	types: string[]
	/** The market label, when the page is scoped to one. */
	marketLabel?: string | null
	onClearMarket?: () => void
	/**
	 * Presigned URL of the brand's logo asset, or null when none is classified (or its
	 * thumbnail has not resolved yet). Null keeps the dashed placeholder.
	 */
	logoUrl?: string | null
	/**
	 * The brand's own tagline. When absent the sample line shows behind its notice, so the
	 * hero never silently loses the row — a brand with no tagline is a gap, not a blank.
	 */
	tagline?: BrandTagline | null
}

/** A tagline plus whether it is the brand's registered line or derived positioning. */
export interface BrandTagline {
	text: string
	/** `false` = wording derived from on-site claims rather than a registered tagline. */
	verbatim?: boolean
}

export function BrandHero({
	name,
	businessUnit,
	species,
	types,
	marketLabel,
	onClearMarket,
	logoUrl,
	tagline,
}: BrandHeroProps) {
	return (
		<CardShell as="section" className="mb-6 p-6">
			<div className="flex flex-col gap-6 sm:flex-row">
				{/* `object-contain` on a padded ground, never `cover`: a wordmark cropped to fill its
				    box is a brand-guideline breach, and most logos are wider than they are tall. */}
				<div
					data-empty={!logoUrl}
					className="flex h-28 w-full shrink-0 items-center justify-center overflow-hidden rounded-ds-md border border-ds-border bg-ds-surface p-3 data-[empty=true]:border-dashed sm:w-44"
				>
					{logoUrl ? (
						<img src={logoUrl} alt={`${name} logo`} className="h-full w-full object-contain" />
					) : (
						<span className="font-mono text-ds-3xs uppercase tracking-ds-widest text-ds-text-subtle">
							{SAMPLE_HERO_LABEL}
						</span>
					)}
				</div>

				<div className="min-w-0 flex-1">
					<Eyebrow>{businessUnit ? `Brand · ${humanizeCode(businessUnit)}` : 'Brand'}</Eyebrow>
					<h1 className="mt-1.5 font-ds-display text-4xl font-semibold leading-tight text-ds-text">{name}</h1>

					<p className="mt-2 flex flex-wrap items-center gap-2 text-ds-text-muted">
						<em>“{tagline?.text ?? SAMPLE_TAGLINE}”</em>
						{/* Three states, not two. A registered tagline is unannotated; one the source
						    DERIVED from on-site claims says so, because presenting editorial wording as
						    the brand's own is the error this flag exists to prevent; no tagline at all
						    keeps the sample notice. */}
						{!tagline ? (
							<SampleDataNotice pending="a brand tagline field" />
						) : tagline.verbatim === false ? (
							<Chip variant="muted" size="sm">
								Positioning
							</Chip>
						) : null}
					</p>

					<div className="mt-4 flex flex-wrap gap-1.5">
						{species.length > 0 ? <Chip variant="muted" size="sm">{`Species: ${species.join(' · ')}`}</Chip> : null}
						{types.length > 0 ? <Chip variant="muted" size="sm">{`Type: ${types.join(' · ')}`}</Chip> : null}
						{marketLabel ? (
							<Chip variant="muted" size="sm" onRemove={onClearMarket} removeLabel={`Remove market ${marketLabel}`}>
								{`Market: ${marketLabel}`}
							</Chip>
						) : null}
					</div>
				</div>
			</div>
		</CardShell>
	)
}
